import {useMemo, useState} from 'react'

import {bookingDayOptions} from './booking-days'
import type {BookingDays, UnassignedJob} from './types'

export type BookingScope = 'every_day' | 'one_day'

type BookingChoice = {key: string; scope: BookingScope; day: string | null}

export function useBookingScope({
    job,
    active,
    bookByDay,
    oneDay,
    viewedDay,
}: {
    job: UnassignedJob
    active: boolean
    bookByDay: boolean
    oneDay: boolean
    viewedDay: string | null
}) {
    const defaultScope: BookingScope = bookByDay ? 'one_day' : 'every_day'
    const [choice, setChoice] = useState<BookingChoice | null>(null)

    if (!active && choice != null) {
        setChoice(null)
    }

    const key = `${job.id}:${defaultScope}`
    const current: BookingChoice =
        choice?.key === key ? choice : {key, scope: defaultScope, day: null}

    const days = useMemo(() => (oneDay ? [] : bookingDayOptions(job)), [job, oneDay])
    const defaultDay =
        bookByDay && viewedDay != null && days.includes(viewedDay) ? viewedDay : (days[0] ?? null)
    const day = current.day != null && days.includes(current.day) ? current.day : defaultDay
    const scope = current.scope
    const pendingDays = useMemo<BookingDays | undefined>(
        () => (scope === 'one_day' && day ? {from: day, until: day} : undefined),
        [day, scope],
    )

    return {
        scope,
        setScope: (next: BookingScope) => setChoice({...current, scope: next}),
        day,
        setDay: (next: string) => setChoice({...current, day: next}),
        days,
        pendingDays,
    }
}
